
import { useEffect, useRef, useState } from "react";
import { useInshurLanding } from "../landingMode";
import Image from "./Image";
import { ArrowRight } from "lucide-react";
import { HomeVerifyNetwork } from "./HomeVerifyNetwork";
import { MarketplaceWaitlistDialog } from "./MarketplaceWaitlistDialog";
import { UsRateMap } from "./UsRateMap";
/* Clause states live in globals.css (.phrase / .phrase-green / .phrase-spectrum /
   .phrase-selected) so the gradients sit alongside the other brand gradients. */
const GREEN = "phrase phrase-green";
const SPECTRUM = "phrase phrase-spectrum";
const AUDIENCES = [
    { label: "Leisure", short: "Leisure", image: "/images/renter/leisure-customers.webp", alt: "Friends loading luggage into a rental SUV for a weekend trip" },
    { label: "Rideshare", short: "Rideshare", image: "/images/renter/gig-rideshare.webp", alt: "A rideshare driver at the wheel with a passenger seated in the rear" },
    { label: "Package Delivery", short: "Package", image: "/images/renter/gig-package.webp", alt: "A delivery driver carrying cardboard packages from a white van to a house door" },
    { label: "Food Delivery", short: "Food", image: "/images/renter/gig-food.webp", alt: "A food delivery courier handing a paper food bag to a customer at a doorstep" },
    { label: "Insurance Replacement", short: "Insurance", image: "/images/renter/insurance-replacement.webp", alt: "A driver receiving keys to a replacement car from a technician at a service center" },
    { label: "Loaner & Courtesy", short: "Loaner", image: "/images/renter/loaner-courtesy.webp", alt: "A customer receiving loaner car keys from an advisor outside a dealership" },
    { label: "Service & Repair", short: "Service", image: "/images/renter/service-repair.webp", alt: "A service advisor reviewing a tablet with a customer beside a car in a dealership service bay", focus: "center 42%" },
    { label: "Hospitality", short: "Hospitality", image: "/images/renter/hospitality.webp", alt: "Guests with luggage arriving at a luxury resort where a courtesy SUV waits" },
    { label: "Property Mobility", short: "Property", image: "/images/renter/property-mobility.webp", alt: "A row of white SUVs parked at the entrance of a luxury residential building at golden hour", focus: "center 38%" },
    { label: "Business Travel", short: "Business", image: "/images/renter/business-travel.webp", alt: "A business traveler with luggage walking to a black SUV outside a corporate tower" },
    { label: "Workforce Mobility", short: "Workforce", image: "/images/renter/workforce-mobility.webp", alt: "Trade workers with a tablet beside a fleet of work vans at a job site" },
    { label: "Long-Term", short: "Long-Term", image: "/images/renter/long-term.webp", alt: "A couple with a duffel bag beside an SUV at a coastal overlook at sunset", focus: "center 68%" },
    { label: "Rent-to-Own", short: "Rent-to-Own", image: "/images/renter/rent-to-own.webp", alt: "A woman holding car keys beside an SUV in front of her home at sunset" },
    { label: "Premium & Occasion", short: "Premium", image: "/images/renter/premium-occasion.webp", alt: "A couple in formalwear beside a black luxury sedan at an event venue" },
    { label: "Speciality", short: "Speciality", image: "/images/renter/speciality-huracan-miami.webp", alt: "A black Lamborghini Huracán parked on Ocean Drive in Miami Beach at golden hour", focus: "center 60%" },
    { label: "Education", short: "Education", image: "/images/renter/education.webp", alt: "A smiling university student with textbooks and a laptop opening the door of a rental SUV on campus", focus: "center 40%" },
];
const brand = (name, dark = false) => ({
    src: `/images/brand-logos/${name}.svg`,
    ...(dark ? { dark: true } : {}),
});
/* `cols` is chosen so every row is full — 8/4, 6/3 and 9/3 all divide evenly, so no
   card ends on a short, ragged row. */
const CHANNELS = [
    {
        label: "Social Media",
        hoverLabel: "Connect your social media",
        cols: 4,
        logos: [
            brand("facebook"),
            brand("instagram"),
            brand("tiktok"),
            brand("youtube"),
            brand("linkedin"),
            brand("x", true),
            brand("reddit"),
            /* Nextdoor has no icon-only variant upstream, so this is the wordmark; `wide`
               lets it use the full tile width instead of being shrunk to icon size. */
            { ...brand("nextdoor"), wide: true },
        ],
    },
    {
        label: "Search and Maps",
        hoverLabel: "List on search and maps",
        cols: 3,
        logos: [
            brand("google"),
            { text: "GBP", color: "#4285F4" },
            brand("google-maps"),
            brand("bing", true),
            brand("apple", true),
            brand("yelp"),
        ],
    },
    {
        /* Mirrors the "Travel & Booking" set in lib/marketing-channels.ts. Kayak,
           Priceline and Travelocity have no mark in any icon registry, so they render as
           lettermarks there and here — the two lists are kept deliberately in step. */
        label: "Online leads",
        hoverLabel: "Funnel online leads",
        cols: 4,
        logos: [
            brand("expedia", true),
            { text: "K", color: "#FF690F" },
            { text: "P", color: "#0068EF" },
            brand("booking"),
            { text: "T", color: "#1478CD" },
            { text: "H", color: "#E4002B" },
            { src: "/images/brand-logos/orbitz.png" },
            brand("tripadvisor"),
        ],
    },
];
/* Shared card chrome: darkening wash on hover, a small resting label bottom-left,
   and the chunky display label that lands in the centre. The visual itself is
   passed in as children so photos, the wordmark card and the tile grids all share
   one hover behaviour. */
function HoverCard({ label, hoverLabel, restLabel, hoverLabelClassName = "text-2xl sm:text-3xl lg:text-[2.5rem]", restClassName = "p-5 pt-12", restLabelClassName = "text-[11px] tracking-widest", className = "", action, children, }) {
    return (<div className={`aud-card relative aspect-[4/3] overflow-hidden rounded-2xl sm:aspect-auto sm:min-h-80 lg:min-h-[26rem] ${className}`}>
      {children}

      {/* Darkening wash, hover only. pointer-events-none on every overlay below so
            they never swallow clicks meant for `action`. */}
      <div aria-hidden="true" className="aud-card__wash pointer-events-none"/>

      {/* Resting label, bottom-left. */}
      <div className={`aud-card__rest pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent ${restClassName}`}>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-[#33F7BE]" aria-hidden="true"/>
          <p aria-label={label} className={`font-sans font-semibold uppercase text-white text-pretty ${restLabelClassName}`}>
            {restLabel ?? label}
          </p>
        </div>
      </div>

      {/* Hover label, centred. aria-hidden so the name isn't announced twice. */}
      <div aria-hidden="true" className="aud-card__mid pointer-events-none absolute inset-0 flex items-center justify-center p-4">
        <p className={`font-display uppercase leading-[0.92] tracking-[-0.02em] text-white text-balance ${hoverLabelClassName}`}>
          {hoverLabel ?? label}
        </p>
      </div>

      {/* Bottom-right, not centred: the resting label occupies the bottom-left and
            fades on hover, whereas the CTA has to stay visible and clickable at all
            times, so the two are kept on opposite corners. */}
      {action ? (<div className="absolute inset-x-0 bottom-0 flex justify-end p-5">{action}</div>) : null}
    </div>);
}
/* The visual layer of an audience card: a cropped photo when one exists, otherwise a
   dark placeholder that keeps the same card chrome (the resting label still shows). */
function AudienceVisual({ audience }) {
    if (audience.image) {
        return (<Image src={audience.image} alt={audience.alt ?? ""} fill priority sizes="(max-width: 640px) 70vw, 22vw" className="aud-card__img object-cover" style={audience.focus ? { objectPosition: audience.focus } : undefined}/>);
    }
    return (<div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(120%_100%_at_30%_20%,#20242C_0%,#111318_60%,#0A0B0E_100%)]">
      <span className="absolute left-5 top-5 font-sans text-[10px] font-medium uppercase tracking-[0.18em] text-white/25">
        Placeholder
      </span>
    </div>);
}
/* "Rent directly to anyone." — two rows of audience cards that auto-scroll in opposite
   directions (top drifts left, bottom drifts right). Each row is rendered twice and its track
   translates by exactly one set width for a seamless loop; cards carry their own right margin
   (no flex gap) so the -50% offset lines up perfectly. Hovering a row pauses its drift, and on
   desktop the hovered card lifts and scales up (.aud-grid-cell) and swaps its single-word rest
   label for the full two-word name. The mask clips horizontally (overflow-x-clip) but stays
   visible vertically so an enlarged card is never cropped. Mobile keeps the drift but gets no
   hover/enlarge (gated in CSS). */
function AudienceRow({ items, direction }) {
    /* The -50% loop is only seamless when ONE half of the track is at least as wide as the
       container; with only ~8-9 cards per row a single set is narrower than the card on wide
       screens, so the set scrolled off before its copy arrived and left a black gap. We build
       each half by repeating the items enough times to always overflow the widest viewport,
       then render that half twice — two identical halves keep the -50% translate gap-free. */
    const HALF_REPEAT = 3;
    const half = Array.from({ length: HALF_REPEAT }, () => items).flat();
    const cards = [...half, ...half];
    return (
    /* flex-1 so the two rows split the fixed AudienceRows height evenly; items-center +
       overflow-y-visible give the hovered card (scale 1.35) room to lift without clipping. */
    <div className="flex min-h-0 flex-1 items-center overflow-x-clip overflow-y-visible">
      {/* boost wrapper: a one-shot ease-out translate on mount. It composes with the inner
            row's steady loop, so the drift starts fast and settles to the base speed as an
            opening sequence (the wrapper's added velocity decays to ~0). */}
      <div className={`aud-boost h-full w-max ${direction === "left" ? "aud-boost--left" : "aud-boost--right"}`}>
        <div className={`aud-row flex h-full w-max items-center ${direction === "left" ? "aud-row--left" : "aud-row--right"}`}>
        {cards.map((a, i) => (
        /* Card size is now driven by row height (h-[86%]) + the 3:4 aspect, so both
           marquee rows fit inside the same height as the platform-state cards. */
        <div key={`${a.label}-${i}`} className="aud-grid-cell relative mr-3 aspect-[3/4] h-[86%] shrink-0 sm:mr-4" aria-hidden={i >= items.length}>
            <HoverCard label={a.label} restLabel={a.short} hoverLabelClassName="text-xs sm:text-sm" restClassName="p-3 pt-10" restLabelClassName="text-[10px] tracking-wide" className="!aspect-auto !h-full !w-full !min-h-0">
              <AudienceVisual audience={a}/>
            </HoverCard>
          </div>))}
        </div>
      </div>
    </div>);
}
/* The two marquee rows are boxed to the SAME height as the platform-state cards
   (sm:min-h-80 / lg:min-h-[26rem]) so the "Rent directly to anyone." view is never taller
   than "Customizable rental platform." Rows share the height via flex-1. */
function AudienceRows() {
    const half = Math.ceil(AUDIENCES.length / 2);
    return (<div className="mt-10 flex h-80 flex-col gap-3 lg:h-[26rem]">
      <AudienceRow items={AUDIENCES.slice(0, half)} direction="left"/>
      <AudienceRow items={AUDIENCES.slice(half)} direction="right"/>
    </div>);
}
/* A grid of soft-black tiles on a soft-black card — nothing rotated and nothing
   cropped, so every mark is fully legible, and it renders exactly the logos it's given
   so no brand is duplicated to pad the grid out.
   The grid fills the card (`h-full` + `auto-rows-fr`) instead of taking its height from
   tile aspect ratio: set sizes differ per card, and deriving height from width made the
   taller sets overflow the card and collide with the label. Bottom padding is deeper
   than the rest to clear the resting label. */
function LogoGrid({ logos, cols }) {
    return (<div aria-hidden="true" className="absolute inset-0 bg-[#141414] p-4 pb-14">
      <div className="logo-grid grid h-full auto-rows-fr gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {logos.map((logo, i) => (
        /* Flip tile: a blank cover flips open to the right (rotateY) on mount to
           uncover the logo. Staggered by index so the grid reveals in sequence.
           `logo-grid` is re-rendered fresh whenever the clause is selected, so the
           flip replays each time the user returns to this section. */
        <div key={logo.src ?? logo.text} className="logo-tile" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="logo-tile__inner">
              <div className="logo-tile__face logo-tile__cover"/>
              <div className="logo-tile__face logo-tile__back p-2">
                {logo.src ? (<Image src={logo.src} alt="" width={64} height={64} 
            /* max-* rather than fixed h/w: tiles are no longer square, so a
               percentage height would distort marks on non-square cells. */
            className={`object-contain ${logo.wide ? "max-h-full w-[88%]" : "max-h-[58%] max-w-[58%]"} ${logo.dark ? "brightness-0 invert" : ""}`}/>) : (<span className="font-sans text-base font-extrabold leading-none tracking-tight lg:text-xl" style={{ color: logo.color }}>
                    {logo.text}
                  </span>)}
              </div>
            </div>
          </div>))}
      </div>
    </div>);
}
/* Distribution channels — shown when "Be discoverable and searchable." is selected. */
function ChannelTileCards() {
    return (<>
      {CHANNELS.map((channel) => (<HoverCard key={channel.label} label={channel.label} hoverLabel={channel.hoverLabel}>
          <LogoGrid logos={channel.logos} cols={channel.cols}/>
        </HoverCard>))}
    </>);
}
/* "Locally and nationally." ������� two equal cards: local demand on the left, the
   national marketplace (rate map + waitlist CTA) on the right. */
function LocalNationalCards() {
    const [waitlistOpen, setWaitlistOpen] = useState(false);
    return (<>
        <HoverCard label="Rent to locals" hoverLabel="Serve the local community">
        <Image src="/images/home/card-rent-to-locals.webp" priority alt="A stylish man in a navy polo and sunglasses photographing a white Tesla Model Y with his phone on an upscale palm-lined shopping street, a brown leather duffle bag beside him" fill sizes="(max-width: 640px) 100vw, 50vw" className="aud-card__img object-cover"/>
      </HoverCard>

        <HoverCard label="Rentid marketplace" hoverLabel="Sign up rentid marketplace" action={<button type="button" onClick={() => setWaitlistOpen(true)} className="rounded-full bg-[#33F7BE] px-5 py-2.5 font-sans text-sm font-semibold text-[#0B0B0B] shadow-lg transition hover:opacity-90">
            Join waitlist
          </button>}>
        <UsRateMap />

        {/* Brand chip, top-left — the marketplace's own mark sitting over its map,
            the way a marketplace badges its listings. */}
        <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/95 px-3.5 py-2 shadow-md">
          <Image src="/logos/rentid-wordmark-black.svg" alt="Rentid" width={64} height={16} className="h-3.5 w-auto"/>
          <span className="font-sans text-xs font-semibold text-[#0B0B0B]">marketplace</span>
        </div>
      </HoverCard>

      <MarketplaceWaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen}/>
    </>);
}
/* A fanned deck of rental-site mockups, spread in 3D perspective like a spread hand of
   cards. Each panel shares the same rotateY tilt and steps to the right, shrinking and
   dimming as it recedes, so the front-left panel reads as the "hero" site. Fills the
   card behind the HoverCard label. */
const RENTAL_SITES = [
    { src: "/images/home/rental-sites/site-1.webp", label: "Minimal rental site", radius: "rounded-lg" },
    { src: "/images/home/rental-sites/site-2.webp", label: "Luxury rental site", radius: "rounded-lg" },
    { src: "/images/home/rental-sites/site-3.webp", label: "Marketplace rental site", radius: "rounded-lg" },
    { src: "/images/home/rental-sites/site-4.webp", label: "Editorial booking site", radius: "rounded-lg" },
    // Mobile-device panel gets a phone-like larger corner radius.
    { src: "/images/home/rental-sites/site-5.webp", label: "Rental booking app", radius: "rounded-3xl" },
];
function RentalSiteFan() {
    return (<div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(120%_100%_at_30%_20%,#20242C_0%,#111318_60%,#0A0B0E_100%)]">
      <div className="absolute inset-0 flex items-center justify-center" style={{ perspective: "2400px" }}>
        {/* The deck fans leftward from the large right-hand card, so shift the whole
            group right to re-centre its visual bounding box within the container. */}
        <div className="relative h-[138%] w-[46%] translate-x-[24%]" style={{ transformStyle: "preserve-3d" }}>
          {RENTAL_SITES.map((site, i) => (<div key={site.src} className="absolute inset-0" style={{
                transform: `translateY(-50%) translateX(${i * -29}%) rotateY(38deg) scale(${1 - i * 0.17})`,
                transformOrigin: "right center",
                top: "50%",
                zIndex: RENTAL_SITES.length - i,
                filter: `brightness(${1 - i * 0.1})`,
            }}>
              {/* Inner wrapper carries the ripple pulse so it composes with the fan
                transform above; the delay staggers the wave across the deck. */}
              <div className="fan-ripple" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className={`relative h-full w-full overflow-hidden ${site.radius} ring-1 ring-white/10 shadow-[0_30px_70px_-15px_rgba(0,0,0,0.8)]`}>
                  <Image src={site.src || "/placeholder.svg"} alt="" fill priority sizes="20vw" className="object-cover"/>
                </div>
              </div>
            </div>))}
        </div>
      </div>
    </div>);
}
/* The three handover cards, shown for every clause except the first and third. */
function ChannelCards() {
    return (<>
      {/* Rental platform: the host portal recording loops inside a landscape monitor frame
          (booking engine + fleet management dashboard). The file is "Host portal final.mov"
          compressed to a silent 1600px H.264 MP4 already running at 1.3x (owner: eye-catch, not a
          sit-and-watch video); the poster is its first frame. */}
      <HoverCard label="Rental Platform" hoverLabel="Fleet mgmt, booking engine, CRM, agreements, payments, and rental workflows">
        <div aria-hidden="true" className="absolute inset-0 bg-[#1C1C1C]"/>
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="relative aspect-video w-full max-w-[22rem] rounded-xl border border-white/10 bg-[#111111] p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.85)]">
            <div className="relative h-full w-full overflow-hidden rounded-lg bg-white">
              <LoopVideo src={HOST_PORTAL_VIDEO} poster="/images/home/host-portal-poster.webp" label="The Rentid host portal: bookings, fleet and claims dashboards"/>
            </div>
          </div>
        </div>
      </HoverCard>

      {/* Branded rental sites — a fanned deck of site mockups. */}
      <HoverCard label="Branded storefronts" hoverLabel="Customizable, branded rental website & embed link included">
        <RentalSiteFan />
      </HoverCard>

      {/* Guest app: a looping screen recording of the Rentid guest app inside the phone frame.
            540x1140 H.264, re-encoded from "RentID Mobile Blur 4.mov" (the Blob original is a
            35MB QuickTime with PCM audio that Chrome/Firefox may refuse) to a 4.2MB silent MP4 at
            CRF 16, visually identical to the source (SSIM 0.999, PSNR 51 dB), served from Vercel
            Blob (media/rentid-guest-app.mp4). The poster is its first frame, so the phone never
            shows empty while the video loads. */}
      {/* Bigger phone (2026-09-29): it fills the card from just under the top edge to just above
          the "Guest account" label strip, and the card is square on small screens (it was a short
          4:3 there, which left the phone ~76px wide). */}
      <HoverCard label="Guest account" hoverLabel="Guests can message you, manage their trips, access insurance cards, & more" className="max-sm:!aspect-square">
        <div aria-hidden="true" className="absolute inset-0 bg-[#1C1C1C]"/>
        <div className="absolute inset-x-0 top-0 bottom-10 flex items-center justify-center px-4 pt-7 pb-4 sm:pt-9 sm:pb-5">
          <div className="relative aspect-[9/19] h-full rounded-[2rem] border border-white/10 bg-[#111111] p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.85)]">
            <div className="relative h-full w-full overflow-hidden rounded-[1.6rem] bg-white">
              <GuestAppVideo />
              {/* Status bar notch. */}
              <div className="pointer-events-none absolute left-1/2 top-2.5 h-1.5 w-12 -translate-x-1/2 rounded-full bg-black/80"/>
            </div>
          </div>
        </div>
      </HoverCard>
    </>);
}
/* Silent looping guest-app recording for the "Guest account" phone. Muted + playsInline so
   every browser autoplays it; users who prefer reduced motion get the still poster instead. */
/* Silent, looping, autoplaying card video; users who prefer reduced motion get the still poster. */
function LoopVideo({ src, poster, label, className = "object-cover object-top" }) {
    const ref = useRef(null);
    useEffect(() => {
        const v = ref.current;
        if (v && window.matchMedia("(prefers-reduced-motion: reduce)").matches) v.pause();
    }, []);
    return (<video ref={ref} className={`absolute inset-0 h-full w-full ${className}`} src={src} poster={poster} autoPlay muted loop playsInline preload="auto" aria-label={label}/>);
}
/* Served from Vercel Blob (media/host-portal.mp4), like the guest-app video. */
const HOST_PORTAL_VIDEO = "https://ti6mkfot02n3tn3d.public.blob.vercel-storage.com/media/host-portal.mp4";
function GuestAppVideo() {
    return (<LoopVideo src="https://ti6mkfot02n3tn3d.public.blob.vercel-storage.com/media/rentid-guest-app.mp4" poster="/images/home/rentid-guest-app-poster.webp" label="The Rentid guest app: upcoming trips, trip details and directions"/>);
}
const CLAUSES = [
    { text: "Rent directly to anyone.", tone: GREEN },
    { text: "Customizable rental platform.", tone: SPECTRUM },
    { text: "Everything built in.", tone: SPECTRUM },
    { text: "Discoverable and searchable.", tone: SPECTRUM },
];
/* Container matches the header/hero (max-w-7xl + same gutters) so the cards line
   up with the logo and the black margins stay narrow. */
export function HomeChannels() {
    const inshur = useInshurLanding(); // /inshur: no off-page links
    /* Clauses are selected ON CLICK ONLY — there is no auto-rotation and no scroll-driven
       default swap. Clause 1 ("Customizable rental platform.") is the resting default from the
       start; nothing changes until the user clicks a clause. */
    const [selected, setSelected] = useState(1);
    /* Whether the current selection came from a user click. Before any click the selected
       clause shows its own gradient; a user click turns the clause solid brand green and
       starts nudging the NEXT clause (see clause-nudge) to hint there's more to explore. */
    const [userPicked, setUserPicked] = useState(false);
    /* Set once the section has scrolled into view (and clause 0 has been revealed). We start
       nudging the next clause from this point on — the user doesn't have to click first. */
    const [revealed, setRevealed] = useState(false);
    const sectionRef = useRef(null);
    useEffect(() => {
        const el = sectionRef.current;
        if (!el)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                /* Small delay before we start nudging the next clause, so it reads as a hint
                   that appears after arrival rather than on entry. The selected clause itself
                   does not change on scroll. */
                const timeout = window.setTimeout(() => {
                    setRevealed(true);
                }, 700);
                observer.disconnect();
                el.dataset.rotationTimeout = String(timeout);
            }
        }, { threshold: 0.4 });
        observer.observe(el);
        return () => {
            observer.disconnect();
            if (el.dataset.rotationTimeout)
                window.clearTimeout(Number(el.dataset.rotationTimeout));
        };
    }, []);
    /* Once the section is revealed (or the user picks a clause), gently shake the NEXT
       clause to signal it's selectable. Wraps around (after the last clause, clause 0). */
    const nextIndex = (selected + 1) % CLAUSES.length;
    const showAudiences = selected === 0;
    const showEverythingBuiltIn = selected === 2;
    const showChannelTiles = selected === 3;
    /* Every clause except "Rent directly to anyone." (which uses the marquee below)
       renders three equal columns. */
    const gridClass = "mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3";
    return (<section ref={sectionRef} className="mx-auto w-full max-w-7xl px-5 sm:px-8 md:px-16" aria-labelledby="channels-heading">
      {/* Four selectable clauses. Dim by default, white when selected, and each
            reveals its own gradient on hover (see PHRASE_* classes above). */}
      <h2 id="channels-heading" 
    /* No text-balance: the <br /> controls the break, two clauses per line.
       Balancing would re-wrap the clauses and add a third line. */
    className="font-sans text-2xl font-normal leading-[1.45] tracking-tight text-white/40 sm:text-3xl md:text-[36px]">
        {CLAUSES.map((clause, i) => (<span key={clause.text}>
            <button type="button" onClick={() => {
                setSelected(i);
                setUserPicked(true);
            }} aria-pressed={selected === i} className={`${clause.tone} ${selected === i ? (userPicked ? "phrase-selected" : "phrase-highlight") : ""} ${revealed && i === nextIndex ? "clause-nudge" : ""} text-left md:text-[44px]`}>
              {i === 0 ? (<>
                  {/* RentDirect play mark + "rentdirect" wordmark rendered as text so
                    "rent" reads semi-bold and "direct" regular, both forced white. The
                    mark is sized to the clause cap height (~1em). */}
                  <Image src="/images/home/rentdirect-mark.svg" alt="" aria-hidden="true" width={72} height={73} priority className="inline-block h-[1em] w-auto translate-y-[0.14em] align-baseline"/>{" "}
                  {/* Force fill-color white so the wordmark never picks up the parent
                    clause's gray/gradient fill (e.g. during the nudge) — always white. */}
                  <span className="text-white" style={{ WebkitTextFillColor: "#ffffff" }}>
                    <span className="font-semibold">rent</span>
                    <span className="font-normal">direct</span>
                  </span>{" "}
                  to anyone.
                </>) : (clause.text)}
            </button>
            {/* Two clauses per line at sm+. `hidden sm:block` is avoided here:
                `display: block` on a <br /> stops it breaking the line at all. */}
            {i === 1 ? <br className="max-sm:hidden"/> : null} {i < CLAUSES.length - 1 ? " " : null}
          </span>))}{" "}
        {!inshur && (<a href="/rentdirect" className="group inline-flex items-center gap-2 align-middle text-base font-normal text-white transition-colors sm:text-xl">
          <span className="text-[26px] underline decoration-white/40 underline-offset-4 transition-colors group-hover:decoration-white">
            Learn more
          </span>
          <ArrowRight aria-hidden="true" className="learn-more-arrow h-[26px] w-[26px] shrink-0"/>
        </a>)}
      </h2>

      {showAudiences ? (<AudienceRows />) : showEverythingBuiltIn ? (
        /* Row: narrow phone-shaped Rentid card, then the two wide cards side by side
           (Rental Back Office in the middle, Direct Rental on the right). */
        <BuiltInDeck />) : (<div className={gridClass}>
          {showChannelTiles ? <ChannelTileCards /> : <ChannelCards />}
        </div>)}
    </section>);
}
/* The "Everything built in" deck plays a scripted intro when it mounts (which is exactly
   when the clause is selected). One large opaque card the same color as the rentID card
   fills the entire deck area, then:
     1) reveal  — the copy builds up unit by unit (first two lines, then the words
                  "business," / "fleet" / "and" / "customers." one at a time, then the
                  brand-green "All in one place."),
     2) logo    — the copy fades out and the shimmering rentID wordmark fades in,
     3) reshape — the SAME card physically reshapes (its width shrinks) down to the small
                  left slot in one smooth transition, revealing the network card to its
                  right. No copy is shown during this transition.
     4) live    — the overlay unmounts, the real (identical) left card takes over, and
                  `.seq-on` arms the build sequence, which then runs as before.
   The final resting rentID card shows its shimmering wordmark; hover still reveals copy. */
const BRAND_GREEN = "#33F7BE";
// The card carries speed almost all the way to the slot, then decelerates in a SHORT, compact
// finish rather than a long feathered tail — the second control point sits far to the right
// (x2 = 0.72) so the slow-down happens over the final few pixels only. Any ease-out that pulls
// x2 back toward the start (like the old 0.22,1,0.36,1 or a symmetric ease-in-out) stretches
// that slow-down across the last stretch, which is exactly what read as "stuck at the end".
const RESHAPE_MS = 560;
const RESHAPE_EASE = "cubic-bezier(0.5, 0, 0.72, 1)";
const LEFT_SLOT_PX = 256; // matches the resting left card's md:w-64 (16rem)
function BuiltInDeck() {
    const [phase, setPhase] = useState("logo");
    const deckRef = useRef(null);
    // Horizontal shrink ratio for the desktop reshape (left slot / full deck width). Null on
    // mobile / before measurement, where we skip the horizontal-shrink entirely.
    const [shrink, setShrink] = useState(null);
    useEffect(() => {
        const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            setPhase("live");
            return;
        }
        // Only the desktop layout has a narrow left slot to shrink into; measure the deck once so we
        // can drive the shrink with a GPU-composited transform (scaleX) instead of animating width
        // (which reflows every frame and made the card stutter/stick as it reached the small slot).
        const isDesktop = window.matchMedia("(min-width: 768px)").matches;
        const deckWidth = deckRef.current?.getBoundingClientRect().width ?? 0;
        if (isDesktop && deckWidth > LEFT_SLOT_PX) {
            setShrink(LEFT_SLOT_PX / deckWidth);
        }
        const timers = [];
        // Hold the logo, reshape, then swap to the real card only AFTER the transform finishes.
        timers.push(setTimeout(() => setPhase("reshape"), 900));
        timers.push(setTimeout(() => setPhase("live"), 900 + RESHAPE_MS + 60));
        return () => timers.forEach(clearTimeout);
    }, []);
    const live = phase === "live";
    const reshape = phase === "reshape";
    // Arm the product build sequence the moment the card STARTS shrinking (reshape), not after it
    // finishes (live). Both the CSS `.seq-on` animations and the people-entrance effect key off
    // this class, so moving it earlier starts the whole product effect in step with the shrink —
    // the logos light up and the beams begin as the card reveals the network, shaving the dead
    // gap that used to sit between "card small" and "effect starts". The overlay hand-off below
    // still waits for `live`, so the reveal itself is unchanged.
    const armed = reshape || live;
    return (<div ref={deckRef} className="relative mt-10 overflow-hidden">
      {/* Real two-column deck sits underneath the overlay the whole time; the overlay
            reveals the network card as it reshapes to the left. */}
      <div className={`flex flex-col gap-3 md:flex-row md:items-stretch${armed ? " seq-on" : ""}`}>
        {/* Left: rentID card at its resting state — shimmering wordmark, hover reveals copy. */}
        <div className="group relative order-last w-full overflow-hidden rounded-2xl bg-[#1C1C1C] md:order-none md:aspect-[9/16] md:w-64 md:flex-none" tabIndex={0}>
          <div className="absolute inset-0 hidden items-center justify-center p-8 transition-opacity duration-300 group-hover:opacity-0 group-focus-visible:opacity-0 md:flex">
            <div className="logo-shimmer h-10 w-28 lg:h-12" role="img" aria-label="Rentid">
              <span aria-hidden="true" className="logo-shimmer__sheen seq-rentid-sheen"/>
            </div>
          </div>
          {/* Mobile: the copy is always shown in a compact card sized to the message.
            Desktop: it fades in over the wordmark on hover. */}
          <div className="pointer-events-none flex items-center justify-start p-6 md:absolute md:inset-0 md:p-7 md:opacity-0 md:transition-opacity md:duration-300 md:group-hover:opacity-100 md:group-focus-visible:opacity-100">
            <p className="text-balance text-left font-display uppercase leading-[0.95] tracking-[-0.02em] text-white text-lg sm:text-xl md:text-2xl">
              Automated platform empowering your business, fleet &amp; customers.{" "}
              <span style={{ color: BRAND_GREEN }}>All in one place.</span>
            </p>
          </div>
        </div>

        {/* Right: built-in suite + direct-rental network. */}
        <div className="relative aspect-[6/7] overflow-hidden rounded-2xl bg-[radial-gradient(120%_100%_at_30%_20%,#20242C_0%,#111318_60%,#0A0B0E_100%)] sm:aspect-auto sm:min-h-80 md:min-h-[26rem] md:flex-1">
          <HomeVerifyNetwork />
        </div>
      </div>

      {/* Intro overlay: one opaque card filling the deck. It NEVER fades its opacity and it
            NEVER animates width. The dark card shrinks to the left slot via a GPU-composited
            scaleX transform (no per-frame layout), and the wordmark counter-scales so it glides
            left at a constant, undistorted size — which removes the mid-shrink stutter. */}
      {!live ? (<div aria-hidden="true" className="pointer-events-none absolute inset-0 z-40">
          <div className="absolute inset-y-0 left-0 w-full origin-left rounded-2xl bg-[#1C1C1C] [backface-visibility:hidden] [will-change:transform]" style={{
                transition: `transform ${RESHAPE_MS}ms ${RESHAPE_EASE}`,
                transform: reshape && shrink != null ? `scaleX(${shrink})` : "scaleX(1)",
            }}>
            {/* rentID wordmark — counter-scaled so it stays crisp and constant-width while the
                card shrinks; centered in the card, so it ends centered in the small left slot. */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="origin-center [will-change:transform]" style={{
                transition: `transform ${RESHAPE_MS}ms ${RESHAPE_EASE}`,
                transform: reshape && shrink != null ? `scaleX(${1 / shrink})` : "scaleX(1)",
            }}>
                <div className="logo-shimmer h-10 w-28 lg:h-12" role="img" aria-label="Rentid">
                  <span aria-hidden="true" className="logo-shimmer__sheen"/>
                </div>
              </div>
            </div>
          </div>
        </div>) : null}
    </div>);
}
/* Bare "everything built in" deck (rentID card + product network, no section wrapper) for
   embedding inside product bands on partner pages (/tnt-group). */
export function HomeBuiltInDeckBare() {
    return <BuiltInDeck />;
}
/* Rental Platform / Branded storefronts / Guest account cards for reuse in product bands. */
export function HomeChannelCards() {
    return (<div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <ChannelCards />
    </div>);
}
/* Parking lot: the former "Locally and nationally." clause cards (local demand +
   national marketplace), relocated to the bottom of the page for reuse later. */
export function HomeMarketplaceParkingLot() {
    return (<section className="mx-auto w-full max-w-7xl px-5 sm:px-8 md:px-16" aria-label="Marketplace">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <LocalNationalCards />
      </div>
    </section>);
}
